import { Component, ChangeDetectionStrategy, inject, computed, signal, input, output } from "@angular/core";
import { toSignal, takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import { TranslateModule } from "@ngx-translate/core";

import { ButtonModule } from "primeng/button";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { MessageModule } from "primeng/message";

import { AppError } from "src/app/store/app-store-error.types";
import { selectQuoteId } from "src/app/store/pages/quote/quote.selectors";
import { selectVersionId } from "src/app/store/pages/quote/version/version.selectors";
import { CreditCheckActions } from "src/app/store/application/data/credit-check/credit-check.actions";
import {
    selectCreditCheckOutcome,
    selectQuoteCreditCheck,
    selectQuoteTotalIncVat,
    selectRerunWouldHitProvider,
} from "src/app/store/application/data/credit-check/quote/quote-credit-check.selectors";
import {
    CreditCheckOutcome,
    isDeniedByLimit,
    isDeniedByScore,
} from "src/app/store/application/data/credit-check/quote/decide-credit-check";
import { QuoteCreditCheckStatusComponent } from "src/app/pages/quote/components/quote-credit-check-status/quote-credit-check-status.component";
import { CoreCurrencyPipe } from "src/app/shared/pipe/core-currency.pipe";

/** Which decline label to show — the two flows word it differently. */
export type CreditCheckStepMode = "send" | "order";

type StepState = "ask" | "running" | "approved" | "denied" | "error";

/**
 * The credit-check step inside the send-quote and create-order steppers.
 *
 * Emits `proceed` when the flow may continue — which includes declining the check, since running
 * one is not mandatory yet — and `cancelled` when the user backs out.
 */
@Component({
    selector: "app-credit-check-step",
    templateUrl: "./credit-check-step.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonModule,
        ProgressSpinnerModule,
        MessageModule,
        TranslateModule,
        QuoteCreditCheckStatusComponent,
        CoreCurrencyPipe,
    ],
})
export class CreditCheckStepComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    /** Which flow is hosting this step — only the decline wording differs. */
    public readonly stepMode = input<CreditCheckStepMode>("send");

    /** The hosting stepper owns navigation, so outcomes are emitted rather than closing anything. */
    public readonly proceed = output<void>();
    public readonly cancelled = output<void>();
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    private readonly quoteId = toSignal(this.store.select(selectQuoteId), { initialValue: 0 });
    private readonly versionId = toSignal(this.store.select(selectVersionId), { initialValue: 0 });
    public readonly creditCheck = toSignal(this.store.select(selectQuoteCreditCheck));
    public readonly outcome = toSignal(this.store.select(selectCreditCheckOutcome));
    public readonly currentTotal = toSignal(this.store.select(selectQuoteTotalIncVat), { initialValue: 0 });
    public readonly rerunHitsProvider = toSignal(this.store.select(selectRerunWouldHitProvider), { initialValue: true });

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    /** Request phase. The three non-idle values are mutually exclusive, so one signal covers them. */
    private readonly phase = signal<"idle" | "running" | "failed" | "notFound">("idle");

    public readonly mode = computed<CreditCheckStepMode>(() => this.stepMode());

    public readonly declineLabelKey = computed(() =>
        this.mode() === "order"
            ? "QUOTE.CREDIT_CHECK.DECLINE_ALREADY_DONE"
            : "QUOTE.CREDIT_CHECK.DECLINE_LATER",
    );

    public readonly state = computed<StepState>(() => {
        const phase = this.phase();
        if (phase === "running") return "running";
        if (phase !== "idle") return "error";

        const outcome = this.outcome();
        // A pre-existing denial is shown immediately — the user cannot proceed and needs to know
        // why, without having to burn another provider lookup to find out. Approved can only be
        // reached after a run here, since the dialog only opens when the outcome is not Approved.
        if (outcome === CreditCheckOutcome.Denied) return "denied";
        if (outcome === CreditCheckOutcome.Approved) return "approved";
        return "ask";
    });

    public readonly isNotFound = computed(() => this.phase() === "notFound");

    /**
     * The denial reasons, rendered inside this step's error message rather than by the status
     * component above it. A single denial shown as two separate blocks of red text reads as two
     * separate problems, which is the opposite of what a user needs here.
     */
    public readonly deniedByScore = computed(() => isDeniedByScore(this.creditCheck()));
    public readonly deniedByLimit = computed(() => isDeniedByLimit(this.creditCheck(), this.currentTotal()));

    constructor() {
        this.actions$.pipe(ofType(CreditCheckActions.runQuoteCheckSuccess), takeUntilDestroyed())
            .subscribe(() => this.phase.set("idle"));

        this.actions$.pipe(ofType(CreditCheckActions.runQuoteCheckFailure), takeUntilDestroyed())
            .subscribe(({ error }) => this.phase.set(this.isCompanyNotFound(error) ? "notFound" : "failed"));
    }

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public runCheck(): void {
        this.phase.set("running");
        this.store.dispatch(CreditCheckActions.runQuoteCheck({
            quoteId: this.quoteId(),
            versionId: this.versionId(),
        }));
    }

    /** Continue the flow: approved, declined, or a super user pushing past a denial. */
    public onProceed(): void {
        this.finish(true);
    }

    /** Override: recorded for audit; the server gate requires the override stamp before passing. */
    public overrideAndProceed(): void {
        this.store.dispatch(CreditCheckActions.overrideQuoteCheck({ quoteId: this.quoteId() }));
        this.finish(true);
    }

    public cancel(): void {
        this.finish(false);
    }

    private finish(shouldProceed: boolean): void {
        shouldProceed ? this.proceed.emit() : this.cancelled.emit();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    /**
     * A company missing from the provider's register is not a denial — the user may continue.
     * Reads the normalized AppError: the raw NSwag ApiException carries a generic message with the
     * server body on .response, so matching the raw message would never fire.
     */
    private isCompanyNotFound(error: AppError | null | undefined): boolean {
        return error?.status === 404 || (error?.message ?? "").includes("COMPANY_NOT_FOUND");
    }
}
